import { useAppTheme } from "@hooks/useAppTheme";
import Box from "@memorang/ui/src/components/Box";
import { useToast } from "@memorang/ui/src/components/Toast";
import { Image } from "expo-image";
import { useRouter } from "expo-router";
import { IconButton, List, Text } from "react-native-paper";
import { Chip } from "react-native-paper";
import usePodcastPlayer from "../hooks/usePodcastPlayer";
import { usePodcastPlayerStore } from "../hooks/usePodcastPlayerStore";
import { useSavedPodcastsCache } from "../hooks/useSavedPodcastsCache";
import type { Document } from "../types";

type PodcastListItemProps = {
	podcast: Document;
};

export const PodcastListItem = ({ podcast }: PodcastListItemProps) => {
	const router = useRouter();
	const theme = useAppTheme();
	const { playPause, handleStartPlay } = usePodcastPlayer();
	const isPlaying = usePodcastPlayerStore((store) => store.isPlaying);
	const currentTrackInfo = usePodcastPlayerStore(
		(store) => store.currentTrackInfo,
	);
	const isPodcastPlaying =
		isPlaying && podcast.content.title === currentTrackInfo?.title;

	const { isPodcastSaved, toggleSavedPodcast } = useSavedPodcastsCache();
	const { showToast } = useToast();

	const handlePodcastPress = () => {
		router.push(`/podcasts/${podcast.id}`);
	};

	const handlePlayPause = () => {
		if (podcast.content.title === currentTrackInfo?.title) {
			playPause();
		} else {
			handleStartPlay(podcast.id);
		}
	};

	const handleBookmarkToggle = () => {
		toggleSavedPodcast(podcast.id);
		showToast({
			message: isPodcastSaved(podcast.id)
				? "Podcast removed from bookmarks"
				: "Podcast added to bookmarks",
			action: {
				label: "Undo",
				action: () => toggleSavedPodcast(podcast.id),
			},
		});
	};

	return (
		<List.Item
			title={() => <Text variant="labelMedium">{podcast.metadata.author}</Text>}
			titleNumberOfLines={1}
			description={() => (
				<Box gap={6}>
					<Text variant="bodyLarge">{podcast.content.title}</Text>
					<Box flexDirection="row" flexWrap="wrap" gap={8}>
						{podcast.tags?.map((tag) => (
							<Chip
								compact
								style={{
									borderRadius: 8,
								}}
								key={tag.id}
								textStyle={{
									...theme.fonts.labelLarge,
									fontWeight: "bold",
								}}
							>
								{tag.label}
							</Chip>
						))}
					</Box>
				</Box>
			)}
			left={(props) => (
				<List.Icon
					{...props}
					icon={() => (
						<Image
							source={{ uri: podcast.metadata.thumbnail }}
							style={{
								width: 80,
								height: 80,
								borderWidth: 0.5,
								borderColor: theme.colors.outline,
								borderRadius: theme.roundness,
							}}
							contentFit="cover"
						/>
					)}
				/>
			)}
			right={() => {
				return (
					<Box flexDirection="row" alignItems="center" gap={8}>
						<IconButton
							icon={isPodcastPlaying ? "pause" : "play"}
							iconColor={theme.colors.onPrimary}
							containerColor={theme.colors.primary}
							size={16}
							onPress={handlePlayPause}
							style={{ margin: 0 }}
						/>
						<IconButton
							mode="contained"
							size={16}
							icon={
								isPodcastSaved(podcast.id) ? "bookmark" : "bookmark-outline"
							}
							onPress={handleBookmarkToggle}
							style={{ margin: 0 }}
						/>
					</Box>
				);
			}}
			onPress={handlePodcastPress}
		/>
	);
};
